// frontend/apps/widgets/src/widgets/GiftVoucher/utils/voucherSorting.js
/**
 * Vérifie si un voucher est featured (compatible avec les deux formats de données)
 * @param {Object} voucher - Voucher depuis l'API widget
 * @return {boolean} True si le voucher est featured
 */
export const isVoucherFeatured = (voucher) => {
  // Nouveau format (après mapper)
  if (voucher.display?.isHighlighted !== undefined) {
    return voucher.display.isHighlighted;
  }
  // Ancien format (compatibilité)
  return voucher.isHighlighted || false;
};

/**
 * Obtient le prix minimum d'un voucher (adapté pour l'API widget)
 * @param {Object} voucher - Voucher depuis l'API widget
 * @return {number} Prix minimum en centimes
 */
export const getMinVoucherPrice = (voucher) => {
  if (voucher.amount?.type === 'free') {
    return 0;
  }

  if (voucher.amount?.type === 'variant' && voucher.amount?.variants?.length > 0) {
    const activeVariants = voucher.amount.variants.filter((v) => v.isActive !== false);
    return activeVariants.length > 0 ?
      Math.min(...activeVariants.map((v) => v.amount || 0)) :
      Infinity;
  }

  // Pour les bons à prix fixe (nouveau format)
  if (voucher.amount?.value) {
    return voucher.amount.value;
  }

  // Ancien format (compatibilité)
  if (voucher.amountType === 'variant' && voucher.variants?.length > 0) {
    const activeVariants = voucher.variants.filter((v) => v.isActive !== false);
    return activeVariants.length > 0 ?
      Math.min(...activeVariants.map((v) => v.fixedAmount || 0)) :
      Infinity;
  }

  return voucher.fixedAmount || 0;
};

/**
 * Obtient le prix maximum d'un voucher (pour tri décroissant)
 * @param {Object} voucher - Voucher depuis l'API widget
 * @return {number} Prix maximum en centimes
 */
export const getMaxVoucherPrice = (voucher) => {
  if (voucher.amount?.type === 'free') {
    return voucher.amount?.max || Infinity;
  }

  if (voucher.amount?.type === 'variant' && voucher.amount?.variants?.length > 0) {
    const activeVariants = voucher.amount.variants.filter((v) => v.isActive !== false);
    return activeVariants.length > 0 ?
      Math.max(...activeVariants.map((v) => v.amount || 0)) :
      0;
  }

  // Pour les bons à prix fixe (nouveau format)
  if (voucher.amount?.value) {
    return voucher.amount.value;
  }

  // Ancien format (compatibilité)
  if (voucher.amountType === 'variant' && voucher.variants?.length > 0) {
    const activeVariants = voucher.variants.filter((v) => v.isActive !== false);
    return activeVariants.length > 0 ?
      Math.max(...activeVariants.map((v) => v.fixedAmount || 0)) :
      0;
  }

  return voucher.fixedAmount || 0;
};

/**
 * Calcule la fourchette de prix globale des vouchers
 * @param {Array} vouchers - Liste des vouchers
 * @return {Object} {min, max} en euros
 */
export const calculatePriceRange = (vouchers) => {
  if (!vouchers || vouchers.length === 0) {
    return {min: 0, max: 500};
  }

  let maxPrice = 0;

  vouchers.forEach((voucher) => {
    if (voucher.amount?.type === 'variant' && voucher.amount?.variants) {
      voucher.amount.variants.forEach((variant) => {
        if (variant.amount && variant.amount > maxPrice) {
          maxPrice = variant.amount;
        }
      });
    } else if (voucher.amount?.value && voucher.amount.value > maxPrice) {
      maxPrice = voucher.amount.value;
    } else if (voucher.amount?.max && voucher.amount.max > maxPrice) {
      maxPrice = voucher.amount.max;
    } else if (voucher.amountType === 'variant' && voucher.variants) {
      voucher.variants.forEach((variant) => {
        if (variant.fixedAmount && variant.fixedAmount > maxPrice) {
          maxPrice = variant.fixedAmount;
        }
      });
    } else if (voucher.fixedAmount && voucher.fixedAmount > maxPrice) {
      maxPrice = voucher.fixedAmount;
    }
  });

  // Convertir de centimes en euros et arrondir
  const maxPriceEuros = maxPrice / 100;
  const roundedPrice = Math.ceil(maxPriceEuros / 50) * 50;
  const finalMax = Math.max(roundedPrice, 100);

  return {
    min: 0,
    max: finalMax,
  };
};

/**
 * Fonction de tri interne (sans considération du featured)
 * @param {Array} vouchers - Liste des vouchers
 * @param {string} sortBy - Critère de tri
 * @return {Array} Vouchers triés
 */
const applySorting = (vouchers, sortBy = 'default') => {
  if (!vouchers || vouchers.length === 0) return [];

  return [...vouchers].sort((a, b) => {
    switch (sortBy) {
      case 'default':
      case 'price_asc': {
        // D'abord trier par type (value avant service) - compatible avec les deux formats
        const typeA = a.type || a.voucherType || 'value';
        const typeB = b.type || b.voucherType || 'value';

        if (typeA !== typeB) {
          return typeA === 'value' ? -1 : 1;
        }

        // Gérer les bons gratuits
        if ((a.amount?.type === 'free') || (a.amountType === 'free')) return -1;
        if ((b.amount?.type === 'free') || (b.amountType === 'free')) return 1;

        const priceA = getMinVoucherPrice(a);
        const priceB = getMinVoucherPrice(b);

        return priceA - priceB;
      }

      case 'price_desc': {
        const typeA = a.type || a.voucherType || 'value';
        const typeB = b.type || b.voucherType || 'value';

        if (typeA !== typeB) {
          return typeA === 'value' ? -1 : 1;
        }

        if ((a.amount?.type === 'free') || (a.amountType === 'free')) return -1;
        if ((b.amount?.type === 'free') || (b.amountType === 'free')) return 1;

        const priceA = getMaxVoucherPrice(a);
        const priceB = getMaxVoucherPrice(b);

        return priceB - priceA;
      }

      case 'alpha_asc':
        return a.name.localeCompare(b.name);

      case 'alpha_desc':
        return b.name.localeCompare(a.name);

      default:
        const typeA = a.type || a.voucherType || 'value';
        const typeB = b.type || b.voucherType || 'value';

        if (typeA !== typeB) {
          return typeA === 'value' ? -1 : 1;
        }
        return getMinVoucherPrice(a) - getMinVoucherPrice(b);
    }
  });
};

/**
 * Trie les vouchers selon le critère spécifié, avec les featured toujours en premier
 * @param {Array} vouchers - Liste des vouchers
 * @param {string} sortBy - Critère de tri
 * @return {Array} Vouchers triés avec featured en premier
 */
export const sortVouchers = (vouchers, sortBy = 'default') => {
  if (!vouchers || vouchers.length === 0) return [];

  // Séparer les vouchers featured des autres
  const featuredVouchers = vouchers.filter((voucher) => isVoucherFeatured(voucher));
  const regularVouchers = vouchers.filter((voucher) => !isVoucherFeatured(voucher));

  // Trier chaque groupe séparément
  const sortedFeatured = applySorting(featuredVouchers, sortBy);
  const sortedRegular = applySorting(regularVouchers, sortBy);

  // Retourner featured en premier, puis le reste
  return [...sortedFeatured, ...sortedRegular];
};

/**
 * Filtre les vouchers par fourchette de prix
 * @param {Array} vouchers - Liste des vouchers
 * @param {Array} priceRange - [min, max] en euros
 * @return {Array} Vouchers filtrés
 */
export const filterVouchersByPrice = (vouchers, priceRange) => {
  if (!vouchers || !priceRange) return vouchers;

  const [minPrice, maxPrice] = priceRange;

  return vouchers.filter((voucher) => {
    // Nouveau format
    if (voucher.amount?.type === 'free') {
      return true;
    }

    if (voucher.amount?.type === 'variant' && voucher.amount?.variants) {
      return voucher.amount.variants.some((variant) => {
        const variantPriceInEuros = variant.amount / 100;
        return variantPriceInEuros >= minPrice && variantPriceInEuros <= maxPrice;
      });
    }

    // Pour les prix fixes (nouveau format)
    if (voucher.amount?.value) {
      const priceInEuros = voucher.amount.value / 100;
      return priceInEuros >= minPrice && priceInEuros <= maxPrice;
    }

    // Ancien format (compatibilité)
    if (voucher.amountType === 'free') {
      return true;
    }

    if (voucher.amountType === 'variant' && voucher.variants) {
      return voucher.variants.some((variant) => {
        const variantPriceInEuros = variant.fixedAmount / 100;
        return variantPriceInEuros >= minPrice && variantPriceInEuros <= maxPrice;
      });
    }

    // Pour les prix fixes (ancien format)
    const price = voucher.fixedAmount || 0;
    const priceInEuros = price / 100;
    return priceInEuros >= minPrice && priceInEuros <= maxPrice;
  });
};
